import React from "react";
import axios from "axios";
import { Trash2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/store";
import { STATUS_COLORS, fmtDate } from "./adminShared";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export function InquiriesTab({ inquiries, setInquiries, auth }) {
  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/inquiries/${id}`, { status }, auth);
      setInquiries((l) => l.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Status updated");
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, auth);
      setInquiries((l) => l.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-inquiries-list">
      {inquiries.length === 0 && <p className="text-[#8f8571] text-sm py-10 text-center">No inquiries yet.</p>}
      {inquiries.map((q) => (
        <div key={q.id} className="bg-white border border-[#eee6d6] p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-heading text-lg text-[#171310]">{q.name}</p>
                <Badge className={`rounded-none ${STATUS_COLORS[q.status]} hover:${STATUS_COLORS[q.status]}`}>{q.status}</Badge>
                <span className="text-xs text-[#9c9482]">{fmtDate(q.createdAt)}</span>
              </div>
              <p className="text-sm text-[#5c5240] mt-1">{q.email}{q.phone ? ` · ${q.phone}` : ""}</p>
              {q.product && <p className="text-sm text-[#a8842c] mt-1">Piece: {q.product}</p>}
              {q.message && <p className="text-sm text-[#5c5240] mt-2 border-t border-[#f2ecdd] pt-2">{q.message}</p>}
              {(q.photos || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="inquiry-photos">
                  {q.photos.map((p) => {
                    const src = p.startsWith("http") ? p : `${API.replace(/\/api$/, "")}${p}`;
                    return (
                      <a key={p} href={src} target="_blank" rel="noreferrer" data-testid="inquiry-photo">
                        <img src={src} alt="Customer inspiration" className="h-20 w-20 object-cover border border-[#d8cdb5] hover:border-[#a8842c] transition-colors" loading="lazy" />
                      </a>
                    );
                  })}
                </div>
              )}
              {q.flagged && (
                <div className="mt-2.5 border border-[#8c2f1b] bg-[#8c2f1b]/5 px-3 py-2" data-testid="inquiry-risk-banner">
                  <p className="flex items-center gap-1.5 text-xs font-bold text-[#8c2f1b] uppercase tracking-wider">
                    <ShieldAlert size={13} /> Risk flags
                  </p>
                  <p className="text-xs text-[#8c2f1b] mt-0.5">{(q.flagReasons || []).join(" · ")}</p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Select value={q.status} onValueChange={(v) => updateStatus(q.id, v)}>
                <SelectTrigger className="w-[130px] rounded-none border-[#d8cdb5] h-9 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-[#e8e0d0] rounded-none">
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={() => remove(q.id)} className="rounded-none border-[#d8cdb5] h-9 w-9 text-[#8c2f1b] hover:bg-[#8c2f1b] hover:text-white" aria-label="Delete">
                <Trash2 size={15} />
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
